import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import styled from 'styled-components'
import { palette, size } from 'styled-theme'
import { Navigate, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Flex } from '@rebass/grid'
import { RiRobot2Line, RiRobot2Fill, RiHeartsLine, RiHeartsFill, RiUserLine, RiUserFill, RiLogoutBoxRLine } from 'react-icons/ri'
import { MdInstallMobile } from 'react-icons/md'
import { FaCompass, FaRegCompass } from 'react-icons/fa'
import { IoGameController, IoGameControllerOutline } from 'react-icons/io5'
import Text from './components/atoms/Text'
import Image from './components/atoms/Image'
import Button from './components/atoms/Button'
import Tooltip from './components/atoms/Tooltip'
import Modal from './components/molecules/Modal'
import ProgressiveAppHandler, { usePwaInstallPrompt } from './components/molecules/ProgressiveAppHandler'
import WhatsNewHandler from './components/organisms/WhatsNewHandler'
import { LOCAL_STORAGE_KEY_PROFILE_AVATAR_REMINDER_OPENED } from './components/pages/Profile/AvatarSection'
import { handleErrorResponse } from './utlils/general'
import { initialAuthState, useAuth } from './contexts/auth'
import { useLoading } from './contexts/loading'
import { getMe } from './api/users'
import { logout } from './api/auth'
import { getStoredAuth } from './api'
import routes from './routes'
import './App.css'

const menuItems = [
  {
    label: '對話',
    Icon: RiRobot2Fill,
    IconActive: RiRobot2Line,
    path: '/',
  },
  {
    label: '配對',
    Icon: RiHeartsFill,
    IconActive: RiHeartsLine,
    path: '/matches',
  },
  {
    label: '探索',
    Icon: FaCompass,
    IconActive: FaRegCompass,
    path: '/explore',
  },
  {
    label: 'PG',
    Icon: IoGameController,
    IconActive: IoGameControllerOutline,
    path: '/playground',
    adminOnly: true,
  },
]

const AppContainer = styled(Flex)`
  background: ${palette('grayscale', 4)};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
`

const Header = styled.header`
  background: ${palette('primary', 0)};
  padding: 0.25rem 1rem;
  position: fixed;
  height: 30vh;
  top: 0;
  left: 0;
  right: 0;
  @media only screen and (max-width: ${size('mobile')}) {
    padding: 0.5rem;
  }
`

const HeaderButton = styled(Button)`
  padding: 0.5rem;
  @media only screen and (max-width: ${size('mobile')}) {
    padding: 0.25rem;
  }
`
const HeaderButtonText = styled(Text)`
  margin-left: 0.4rem;
  @media only screen and (max-width: ${size('tablet')}) {
    display: none;
  }
`

const ProfileButton = styled(HeaderButton)`
  @media only screen and (max-width: ${size('mobile')}) {
    max-width: 5rem;
  }
`

const DesktopImage = styled(Image)`
  @media only screen and (max-width: ${size('mobile')}) {
    display: none;
  }
`

const MobileImage = styled(Image)`
  display: none;
  @media only screen and (max-width: ${size('mobile')}) {
    display: initial;
  }
`

const AuthRedirectior = ({ required, to, children }) => {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const { auth } = useAuth()

  if (required && Object.prototype.hasOwnProperty.call(required, 'auth')) {
    if (required.auth) {
      if (!auth.user) {
        const locationStr = `${location.pathname}${location.search}`
        return (
          <Navigate
            to={{
              pathname: to,
              ...(locationStr !== '/' ? {
                search: `?redirect=${locationStr}`,
              } : {}),
            }}
            replace
          />
        )
      }

      if (location.pathname !== '/complete-profile') {
        if (auth.user.role < 0) {
          return <Navigate to="/complete-profile" replace />
        }
      } else if (auth.user.role >= 0) {
        return <Navigate to="/" replace />
      }
    } else if (auth.user) {
      return <Navigate to={searchParams.get('redirect') || '/'} replace />
    }
  }
  return children
}

AuthRedirectior.propTypes = {
  required: PropTypes.shape({
    auth: PropTypes.bool,
  }),
  to: PropTypes.string,
  children: PropTypes.node,
}

const App = () => {
  const [installPwaPromptEvent, setInstallPwaPromptEvent] = useState(null)
  const [avatarReminderOpen, setAvatarReminderOpen] = useState(false)
  const [confirmingLogout, setConfirmingLogout] = useState(false)
  const { auth, setAuth } = useAuth()
  const { setLoading } = useLoading()
  const location = useLocation()
  const navigate = useNavigate()

  usePwaInstallPrompt(setInstallPwaPromptEvent)

  const onLogout = useCallback(() => {
    setLoading(true)
    return logout()
      .then(({ data }) => {
        if (get(data, 'status') !== 'success') {
          throw new Error('登出發生錯誤')
        }
        setAuth(initialAuthState)
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [setLoading, setAuth])

  const onCloseAvatarReminder = useCallback(() => {
    setAvatarReminderOpen(false)
    localStorage.setItem(`${auth.user?.username}_${LOCAL_STORAGE_KEY_PROFILE_AVATAR_REMINDER_OPENED}`, true)
    navigate('/profile')
  }, [auth.user, navigate])

  useEffect(() => {
    if (auth.user
       && !auth.user.avatar_url
       && !['/login', '/profile'].includes(location.pathname)
    ) {
      const opened = localStorage.getItem(`${auth.user.username}_${LOCAL_STORAGE_KEY_PROFILE_AVATAR_REMINDER_OPENED}`)
      if (!opened) {
        setAvatarReminderOpen(true)
      }
    }
  }, [auth.user, location.pathname])

  useEffect(() => {
    if (get(getStoredAuth(), 'access_token')) {
      setLoading(true)
      const userParams = {
        eager: 'user_images',
      }
      getMe(userParams)
        .then(({ data: user }) => {
          const updatedAuth = { ...getStoredAuth(), user }
          setAuth(updatedAuth)
        })
        .catch((err) => {
          handleErrorResponse(err, setAuth)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [setLoading, setAuth])

  return (
    <AppContainer>
      <Header>
        <Flex alignItems="center" justifyContent="space-between">
          <Button variant="text" padding="0" href="https://crushk.co">
            <DesktopImage src="/logo.png" height="3.75rem" alt="Crush" />
            <MobileImage src="/logo_simple.png" height="2.75rem" alt="Crush" />
          </Button>
          {!!auth.user && (
            <>
              <Flex alignItems="center">
                {auth.user.role >= 0 && menuItems.map((menuItem, mi) => {
                  const { label, path, Icon, IconActive } = menuItem
                  const isActive = location.pathname === path
                  if (menuItem.adminOnly && !!auth.user.role) {
                    return null
                  }
                  return (
                    <Box key={mi} ml={mi > 0 ? '0.2rem' : 0}>
                      <HeaderButton
                        variant={isActive ? 'outline' : 'text'}
                        palette="grayscale"
                        to={path}
                      >
                        {isActive && !!IconActive ? (
                          <IconActive size={28} />
                        ) : (
                          <Icon size={28} />
                        )}
                        <HeaderButtonText>{label}</HeaderButtonText>
                      </HeaderButton>
                    </Box>
                  )
                })}
                {installPwaPromptEvent && (
                  <Box ml="0.2rem">
                    <HeaderButton
                      variant="text"
                      palette="grayscale"
                      onClick={() => installPwaPromptEvent.prompt()}
                    >
                      <MdInstallMobile size={28} />
                      <HeaderButtonText>下載</HeaderButtonText>
                    </HeaderButton>
                  </Box>
                )}
              </Flex>
              <Flex alignItems="center">
                {auth.user.role >= 0 && (
                  <Box mr="0.2rem">
                    <ProfileButton
                      id="profile-button"
                      variant={location.pathname === '/profile' ? 'outline' : 'text'}
                      palette="grayscale"
                      to="/profile"
                    >
                      {location.pathname === '/profile' ? (
                        <RiUserLine size={28} />
                      ) : (
                        <RiUserFill size={28} />
                      )}
                      <HeaderButtonText>用戶</HeaderButtonText>
                    </ProfileButton>
                  </Box>
                )}
                <HeaderButton palette="grayscale" onClick={() => setConfirmingLogout(true)}>
                  <RiLogoutBoxRLine size={28} />
                  <HeaderButtonText palette="white">登出</HeaderButtonText>
                </HeaderButton>
              </Flex>
            </>
          )}
        </Flex>
      </Header>
      <Flex flex="1 1 0" width={1} justifyContent="center" style={{ overflow: 'scroll' }}>
        <Routes>
          {routes.map((route) => {
            const { path, element } = route
            return (
              <Route
                key={path}
                path={path}
                element={(
                  <AuthRedirectior required={route.required} to="/login">
                    {element}
                  </AuthRedirectior>
                )}
              />
            )
          })}
        </Routes>
      </Flex>
      <Tooltip
        anchorId="profile-button"
        open={avatarReminderOpen}
        place="bottom-end"
      >
        <Text palette="white" variant="body1">
          {'試下搵 Crush仔🤖\n幫你度身訂造一個頭像！'}
        </Text>
        <Flex justifyContent="flex-end" mt={1}>
          <Button
            palette="grayscale"
            padding="0.25rem 0.5rem"
            onClick={onCloseAvatarReminder}
          >
            <Text palette="white" variant="body2">試下！</Text>
          </Button>
        </Flex>
      </Tooltip>
      <Modal
        isOpen={confirmingLogout}
        onClose={() => setConfirmingLogout(false)}
        style={{ width: '100%', maxWidth: 320 }}
        showCloseBtn
      >
        <Flex flexDirection="column" alignItems="center" width={1} p="1rem">
          <Text variant="h3" align="center">確定登出？</Text>
          <Flex mt="2rem" width={1}>
            <Box flex={1}>
              <Button
                fullWidth
                variant="outline"
                palette="error"
                onClick={() => setConfirmingLogout(false)}
              >
                取消
              </Button>
            </Box>
            <Box flex={1} ml="1rem">
              <Button
                fullWidth
                onClick={() => {
                  setConfirmingLogout(false)
                  return onLogout()
                }}
              >
                確定
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Modal>
      <ProgressiveAppHandler />
      <WhatsNewHandler />
    </AppContainer>
  )
}

export default App
